import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import VideoPlayer from "./VideoPlayer";
import {
  buildVideoPlaylist,
  findLessonIndex,
  resolveLessonVideoUrl,
} from "../../utils/videoPlaylist";
import {
  resolveLessonFlag,
  resolveLessonNumber,
} from "../../utils/videoPlayerConfig";

const CourseVideoPlayer = ({
  currentLesson,
  sections,
  createLessonLog,
  lessonLogsRef,
  inVideoQuestionsRef,
  setIsAboveVideoExam,
  setCurrentInVideoQuestion,
  onLessonChange,
  onPlayedSeconds,
  subtitleSegments = null,
}) => {
  const { t } = useTranslation("learningPaths");
  const [pauseSignal, setPauseSignal] = useState(0);
  const lastLoggedSecondRef = useRef(0);
  const playedSecondsRef = useRef(0);

  const videoPlaylist = useMemo(() => buildVideoPlaylist(sections), [sections]);
  const currentVideoIndex = useMemo(
    () => findLessonIndex(videoPlaylist, currentLesson?.id),
    [videoPlaylist, currentLesson?.id],
  );

  const resolvedUrl = resolveLessonVideoUrl(currentLesson);
  const restrictNavigation = resolveLessonFlag(
    currentLesson,
    "restrict_navigation",
    false,
  );
  const isVideoSpeedEnabled = resolveLessonFlag(
    currentLesson,
    "is_video_speed_enabled",
    true,
  );
  const disableVideoSpeed = !isVideoSpeedEnabled;
  const isTenSecondSkipEnabled = resolveLessonFlag(
    currentLesson,
    "is_ten_second_skip_enabled",
    true,
  );
  const disableForwardSeek = !isTenSecondSkipEnabled;
  const enableIntervalProgress = resolveLessonFlag(
    currentLesson,
    "enable_interval_video_progress",
    false,
  );
  const intervalSeconds = Math.max(
    1,
    resolveLessonNumber(
      currentLesson,
      "interval_video_progress_in_seconds",
      10,
    ),
  );

  useEffect(() => {
    lastLoggedSecondRef.current = 0;
    playedSecondsRef.current = 0;
  }, [currentLesson?.id, enableIntervalProgress, intervalSeconds]);

  const handleProgress = async (state) => {
    const currentSecond = Math.floor(state.playedSeconds);
    playedSecondsRef.current = state.playedSeconds;
    onPlayedSeconds?.(state.playedSeconds);

    const currentQuestion = inVideoQuestionsRef.current?.find(
      (question) => question.timeInSeconds === currentSecond,
    );

    if (currentQuestion) {
      const currentLessonLog = lessonLogsRef.current?.find(
        (log) => String(log.lesson_id) === String(currentLesson?.id),
      );
      const answeredBefore = currentLessonLog?.answers?.some(
        (answer) => String(answer.id) === String(currentQuestion.id),
      );

      if (!answeredBefore) {
        setCurrentInVideoQuestion(currentQuestion);
        setIsAboveVideoExam(true);
        setPauseSignal((prev) => prev + 1);
      }
    }

    if (!enableIntervalProgress || !currentLesson?.id || !createLessonLog)
      return;
    if (
      currentSecond <= 0 ||
      currentSecond - lastLoggedSecondRef.current < intervalSeconds
    )
      return;

    const lessonDuration = currentLesson?.video_duration || 0;
    const progress =
      lessonDuration > 0
        ? Math.min(100, (currentSecond / lessonDuration) * 100)
        : 0;

    lastLoggedSecondRef.current = currentSecond;
    await createLessonLog({
      lesson_id: currentLesson.id,
      progress,
      counter: intervalSeconds,
      seconds: currentSecond,
    });
  };

  const handleEnded = async () => {
    if (!currentLesson?.id || !createLessonLog) return;
    await createLessonLog({ lesson_id: currentLesson.id });
  };

  const handlePlaylistIndexChange = (newIndex) => {
    const selected = videoPlaylist[newIndex]?.lesson;
    if (selected) onLessonChange(selected);
  };

  if (!resolvedUrl) return null;

  return (
    <VideoPlayer
      url={resolvedUrl}
      playlist={videoPlaylist.map((item) => ({
        url: item.url,
        name: item.name,
      }))}
      currentPlaylistIndex={currentVideoIndex}
      onPlaylistIndexChange={handlePlaylistIndexChange}
      autoPlayNext
      autoPlay
      onProgress={handleProgress}
      onEnded={handleEnded}
      restrictNavigation={restrictNavigation}
      disableVideoSpeed={disableVideoSpeed}
      disableForwardSeek={disableForwardSeek}
      onSeekBlocked={() => toast.warning(t("cantSeekVideo"))}
      pauseSignal={pauseSignal}
      subtitleSegments={subtitleSegments}
    />
  );
};

export default CourseVideoPlayer;
